import type { ClientExperimentVariantAssignment } from '@/services/experiment';

export const ONBOARDING_UI_EXPERIMENT_KEY = 'onboarding_ui';
export const ONBOARDING_UI_CONTROL_VARIANT = 'control';
export const ONBOARDING_UI_TREATMENT_VARIANT = 'treatment';

export type OnboardingUIVariant =
  | typeof ONBOARDING_UI_CONTROL_VARIANT
  | typeof ONBOARDING_UI_TREATMENT_VARIANT;

export interface OnboardingUIExperience {
  presentation: 'simple-message' | 'upgrade-card';
  showEditModeTutorial: boolean;
  showInitialGenerationFeedback: boolean;
}

const EXPERIENCE_BY_VARIANT: Record<OnboardingUIVariant, OnboardingUIExperience> = {
  [ONBOARDING_UI_CONTROL_VARIANT]: {
    presentation: 'simple-message',
    showEditModeTutorial: true,
    showInitialGenerationFeedback: true,
  },
  [ONBOARDING_UI_TREATMENT_VARIANT]: {
    presentation: 'upgrade-card',
    showEditModeTutorial: false,
    showInitialGenerationFeedback: false,
  },
};

export type OnboardingUIAssignmentDecision =
  | {
      source: 'assignment';
      variant: OnboardingUIVariant;
      exposable: boolean;
      experience: OnboardingUIExperience;
    }
  | {
      source: 'control-fallback';
      exposable: false;
      experience: OnboardingUIExperience;
    };

export function isOnboardingUIVariant(value: unknown): value is OnboardingUIVariant {
  return value === ONBOARDING_UI_CONTROL_VARIANT || value === ONBOARDING_UI_TREATMENT_VARIANT;
}

export function adaptOnboardingUIExperience(
  variant: OnboardingUIVariant,
): OnboardingUIExperience {
  return EXPERIENCE_BY_VARIANT[variant];
}

export function resolveOnboardingUIAssignment(
  assignment: ClientExperimentVariantAssignment | null | undefined,
): OnboardingUIAssignmentDecision {
  if (!isOnboardingUIVariant(assignment?.variantCode)) {
    return {
      source: 'control-fallback',
      exposable: false,
      experience: adaptOnboardingUIExperience(ONBOARDING_UI_CONTROL_VARIANT),
    };
  }

  return {
    source: 'assignment',
    variant: assignment.variantCode,
    exposable: assignment.exposable === true,
    experience: adaptOnboardingUIExperience(assignment.variantCode),
  };
}
